import { z } from 'zod';
import { DocumentType, MaintenanceInterval, WorkOrderStatus } from '../enums.js';

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const isoDateTime = z.string().datetime({ offset: true });
const money = z.number().min(0).max(99_999_999).multipleOf(0.01);

// ───────────────────────────── schedules ─────────────────────────────

export const MaintenanceScheduleInputSchema = z.object({
  vehicleId: z.string().uuid(),
  title: z.string().min(2).max(120),
  intervalType: z.enum(MaintenanceInterval),
  /** km, days or engine hours depending on intervalType */
  intervalValue: z.number().int().positive().max(1_000_000),
  /** how far ahead (same unit) to raise MAINTENANCE_DUE */
  remindBefore: z.number().int().min(0).max(100_000).default(0),
  lastDoneAt: isoDate.nullable().optional(),
  lastDoneOdometerKm: z.number().min(0).max(9_999_999).nullable().optional(),
  /** manual threshold when nothing was done yet (km/hours) */
  nextDueOdometerKm: z.number().min(0).max(9_999_999).nullable().optional(),
  nextDueAt: isoDate.nullable().optional(),
  isActive: z.boolean().default(true),
});
export type MaintenanceScheduleInput = z.infer<typeof MaintenanceScheduleInputSchema>;

export const UpdateMaintenanceScheduleSchema = MaintenanceScheduleInputSchema.partial();
export type UpdateMaintenanceSchedule = z.infer<typeof UpdateMaintenanceScheduleSchema>;

export const MaintenanceDueStatus = ['OK', 'DUE_SOON', 'OVERDUE', 'UNKNOWN'] as const;
export type MaintenanceDueStatus = (typeof MaintenanceDueStatus)[number];

export const MaintenanceQuerySchema = z.object({
  vehicleId: z.string().uuid().optional(),
  status: z.enum(MaintenanceDueStatus).optional(),
  includeInactive: z.coerce.boolean().optional(),
});
export type MaintenanceQuery = z.infer<typeof MaintenanceQuerySchema>;

// ───────────────────────────── work orders ─────────────────────────────

export const WorkOrderPartSchema = z.object({
  name: z.string().min(1).max(120),
  qty: z.number().positive().max(100_000).default(1),
  unitPriceEgp: money.default(0),
});
export type WorkOrderPart = z.infer<typeof WorkOrderPartSchema>;

export const WorkOrderInputSchema = z.object({
  vehicleId: z.string().uuid(),
  scheduleId: z.string().uuid().nullable().optional(),
  title: z.string().min(2).max(160),
  description: z.string().max(2000).nullable().optional(),
  workshopName: z.string().max(120).nullable().optional(),
  odometerKm: z.number().min(0).max(9_999_999).nullable().optional(),
  laborCostEgp: money.default(0),
  /** derived from parts when omitted */
  partsCostEgp: money.optional(),
  parts: z.array(WorkOrderPartSchema).max(100).optional(),
  invoiceFileId: z.string().uuid().nullable().optional(),
  /** flip the vehicle to MAINTENANCE while the order is open */
  setVehicleMaintenance: z.boolean().default(true),
  openedAt: isoDateTime.optional(),
});
export type WorkOrderInput = z.infer<typeof WorkOrderInputSchema>;

export const UpdateWorkOrderSchema = WorkOrderInputSchema.partial();
export type UpdateWorkOrder = z.infer<typeof UpdateWorkOrderSchema>;

export const CompleteWorkOrderSchema = z.object({
  completedAt: isoDateTime.optional(),
  odometerKm: z.number().min(0).max(9_999_999).nullable().optional(),
  laborCostEgp: money.optional(),
  partsCostEgp: money.optional(),
  parts: z.array(WorkOrderPartSchema).max(100).optional(),
  invoiceFileId: z.string().uuid().nullable().optional(),
  /** mark the linked schedule as done at this odometer/date and roll its next due */
  resetSchedule: z.boolean().default(true),
  notes: z.string().max(2000).nullable().optional(),
});
export type CompleteWorkOrder = z.infer<typeof CompleteWorkOrderSchema>;

export const WorkOrdersQuerySchema = z.object({
  status: z.enum(WorkOrderStatus).optional(),
  vehicleId: z.string().uuid().optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
});
export type WorkOrdersQuery = z.infer<typeof WorkOrdersQuerySchema>;

export const WORK_ORDER_TRANSITIONS: Record<(typeof WorkOrderStatus)[number], readonly string[]> = {
  OPEN: ['update', 'start', 'complete', 'cancel', 'delete'],
  IN_PROGRESS: ['update', 'complete', 'cancel'],
  DONE: [],
  CANCELLED: ['delete'],
};

/** Sum of parts lines, rounded to piasters. */
export const partsTotal = (parts: WorkOrderPart[] | null | undefined): number => Math.round((parts ?? []).reduce((s, p) => s + p.qty * p.unitPriceEgp, 0) * 100) / 100;

// ───────────────────────────── documents ─────────────────────────────

export const VehicleDocumentInputSchema = z.object({
  type: z.enum(DocumentType),
  title: z.string().min(2).max(120),
  number: z.string().max(80).nullable().optional(),
  issuedAt: isoDate.nullable().optional(),
  expiresAt: isoDate.nullable().optional(),
  fileId: z.string().uuid().nullable().optional(),
  remindDaysBefore: z.number().int().min(0).max(365).default(30),
});
export type VehicleDocumentInput = z.infer<typeof VehicleDocumentInputSchema>;

export const UpdateVehicleDocumentSchema = VehicleDocumentInputSchema.partial();
export type UpdateVehicleDocument = z.infer<typeof UpdateVehicleDocumentSchema>;

export const DocumentsExpiringQuerySchema = z.object({
  days: z.coerce.number().int().min(0).max(365).default(30),
  vehicleId: z.string().uuid().optional(),
});
export type DocumentsExpiringQuery = z.infer<typeof DocumentsExpiringQuerySchema>;

// ───────────────────────────── due calculator (pure) ─────────────────────────────

export interface ScheduleLike {
  intervalType: (typeof MaintenanceInterval)[number];
  intervalValue: number;
  remindBefore: number;
  lastDoneAt: string | Date | null;
  lastDoneOdometerKm: number | null;
  nextDueAt?: string | Date | null;
  nextDueOdometerKm?: number | null;
}

export interface VehicleMeter {
  odometerKm: number | null;
  engineHours: number | null;
}

export interface DueInfo {
  status: MaintenanceDueStatus;
  /** km / hours / days left (negative when overdue); null when unknown */
  remaining: number | null;
  unit: 'km' | 'h' | 'days';
  nextDueAt: string | null;
  nextDueOdometerKm: number | null;
}

const DAY_MS = 86_400_000;
const toDate = (d: string | Date | null | undefined): Date | null => (d == null ? null : d instanceof Date ? d : new Date(d.length === 10 ? `${d}T00:00:00Z` : d));
const isoDay = (d: Date) => d.toISOString().slice(0, 10);
const todayUtc = (now: Date) => Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());

/**
 * Next due threshold from the last completion. For KM / ENGINE_HOURS the threshold lives in
 * `nextDueOdometerKm` (km or hours); for DAYS it is the date `nextDueAt`.
 * When nothing was done yet the previously stored threshold is kept (set manually at creation).
 */
export function rollNextDue(s: ScheduleLike): { nextDueAt: string | null; nextDueOdometerKm: number | null } {
  if (s.intervalType === 'DAYS') {
    const last = toDate(s.lastDoneAt);
    if (!last) return { nextDueAt: s.nextDueAt ? isoDay(toDate(s.nextDueAt)!) : null, nextDueOdometerKm: null };
    return { nextDueAt: isoDay(new Date(last.getTime() + s.intervalValue * DAY_MS)), nextDueOdometerKm: null };
  }
  if (s.lastDoneOdometerKm == null) return { nextDueAt: null, nextDueOdometerKm: s.nextDueOdometerKm ?? null };
  return { nextDueAt: null, nextDueOdometerKm: Math.round((s.lastDoneOdometerKm + s.intervalValue) * 100) / 100 };
}

/** Compares the schedule threshold with the vehicle's live meter (or today) and classifies it. */
export function dueInfo(s: ScheduleLike, meter: VehicleMeter, now: Date = new Date()): DueInfo {
  const next = rollNextDue(s);
  if (s.intervalType === 'DAYS') {
    const due = toDate(next.nextDueAt);
    if (!due) return { status: 'UNKNOWN', remaining: null, unit: 'days', ...next };
    const remaining = Math.ceil((due.getTime() - todayUtc(now)) / DAY_MS);
    return { status: remaining < 0 ? 'OVERDUE' : remaining <= s.remindBefore ? 'DUE_SOON' : 'OK', remaining, unit: 'days', ...next };
  }
  const unit = s.intervalType === 'KM' ? 'km' : 'h';
  const current = s.intervalType === 'KM' ? meter.odometerKm : meter.engineHours;
  if (next.nextDueOdometerKm == null || current == null) return { status: 'UNKNOWN', remaining: null, unit, ...next };
  const remaining = Math.round((next.nextDueOdometerKm - current) * 100) / 100;
  return { status: remaining < 0 ? 'OVERDUE' : remaining <= s.remindBefore ? 'DUE_SOON' : 'OK', remaining, unit, ...next };
}

/** Days until a date (negative = past); null when absent. Uses the UTC calendar day of `now`. */
export function daysUntil(date: string | Date | null | undefined, now: Date = new Date()): number | null {
  const d = toDate(date);
  if (!d) return null;
  return Math.ceil((d.getTime() - todayUtc(now)) / DAY_MS);
}
